// Customer name, phone and email — the tablet app's rules and wording (one
// difference: names here may also carry a hyphen, e.g. Mary-Jane)
// (cld_merchant/youslotz: core/const/phone.dart AuPhone, core/const/email.dart
// Email, PersonName in common_widgets.dart), so a booking taken on the web and
// one taken on the tablet accept and reject the same details. Keep in step.

// ---- Name ----

const NAME_MIN = 2;
const NAME_MAX = 50;

/** Trim the ends and collapse inner whitespace — the form checked and sent. */
export const normaliseName = (raw = '') => raw.trim().replace(/\s+/g, ' ');

/** Typing filter: only English letters, spaces, a single quote (' or ’) and a hyphen. */
export const filterNameInput = (value) => value.replace(/[^A-Za-z '’-]/g, '');

/** '' when [raw] is a valid name, otherwise the reason, phrased with [label]. */
export const validatePersonName = (raw, label = 'Name') => {
  const v = normaliseName(raw);
  if (!v) return `${label} is required`;
  if (v.length < NAME_MIN) return `${label} must be at least ${NAME_MIN} characters`;
  if (v.length > NAME_MAX) return `${label} must be ${NAME_MAX} characters or fewer`;
  if (!/^[A-Za-z]/.test(v)) return `${label} must start with a letter`;
  if (!/^[A-Za-z '’-]+$/.test(v)) return `${label} can use only letters, spaces, ' and -`;
  if (/(['’])\1/.test(v)) return `${label} cannot repeat '`;
  // A hyphen joins two parts (Mary-Jane): never doubled, never at the end.
  if (/--/.test(v)) return `${label} cannot repeat -`;
  if (/-$/.test(v)) return `${label} cannot end with -`;
  return '';
};

// ---- Phone (any Australian number) ----

/** Longest a phone may be typed: +61412345678 is 12. */
const PHONE_MAX_INPUT = 12;

export const AU_PHONE_HINT = '0412 345 678';
export const AU_PHONE_ERROR = 'Enter a valid Australian phone number (e.g. 0412 345 678 or +61 412 345 678)';

/** Spaces, dashes and brackets removed; a leading + is kept. */
export const normalisePhone = (raw = '') => raw.trim().replace(/[\s\-()]/g, '');

/** 0412 345 678, 02 9876 5432, +61 412 345 678 and 61 2 9876 5432 all pass. */
export const isValidAuPhone = (raw) => /^(?:\+?61|0)\d{9}$/.test(normalisePhone(raw));

/** The digits (and leading +) spaced into the groups the number is heading for. */
const groupPhone = (digits) => {
  let head = '';
  let rest = digits;
  if (digits.startsWith('+61')) {
    head = '+61';
    rest = digits.slice(3);
  } else if (digits.startsWith('61')) {
    head = '61';
    rest = digits.slice(2);
  }
  let sizes;
  if (head) sizes = rest.startsWith('4') || rest.startsWith('5') ? [3, 3, 3] : [1, 4, 4];
  else if (/^(04|05|13|18)/.test(digits)) sizes = [4, 3, 3];
  else if (digits.startsWith('0')) sizes = [2, 4, 4];
  else return digits;

  const parts = head ? [head] : [];
  let at = 0;
  for (let i = 0; i < sizes.length && at < rest.length; i += 1) {
    const end = i === sizes.length - 1 ? rest.length : Math.min(at + sizes[i], rest.length);
    parts.push(rest.slice(at, end));
    at = end;
  }
  return parts.join(' ');
};

/**
 * The typing rule: digits and a leading +, at most 12 of them, spaced as they
 * are typed (0431 134 169, 02 9876 5432, +61 431 134 169). The spaces are only
 * on screen — see phoneForApi.
 */
export const formatPhoneInput = (value) => {
  let kept = value.replace(/[^\d+]/g, '');
  kept = (kept.startsWith('+') ? '+' : '') + kept.replace(/\+/g, '');
  return groupPhone(kept.slice(0, PHONE_MAX_INPUT));
};

/** The nine digits after 0 / 61 / +61. */
export const localPhoneDigits = (raw = '') => {
  const d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('61')) return d.slice(2);
  if (d.length === 10 && d.startsWith('0')) return d.slice(1);
  return d;
};

/** What the API is sent for customer_phone: 61 and the nine digits. */
export const phoneForApi = (raw) => {
  const d = localPhoneDigits(raw);
  return d ? `61${d}` : '';
};

// ---- Email ----

export const EMAIL_MAX = 254;
export const EMAIL_ERROR = 'Enter a valid email address';

/** HTML5's pattern, anchored, with at least one dot in the domain. */
const EMAIL_PATTERN =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

/** Typing filter: no spaces, capped at the RFC limit. */
export const filterEmailInput = (value) => value.replace(/\s/g, '').slice(0, EMAIL_MAX);

/** Whether [raw] is a valid address. An empty one is not — check for optional first. */
export const isValidEmail = (raw = '') => {
  const v = raw.trim().toLowerCase();
  if (!v || v.length > EMAIL_MAX || /\s/.test(v)) return false;
  return EMAIL_PATTERN.test(v);
};

/** Trim and lowercase — the form a new address is sent in, as on the tablet. */
export const normaliseEmail = (raw = '') => raw.trim().toLowerCase();

// ---- Phone: what other forms send, and how a stored phone is shown ----

/**
 * The phone as sent by the forms outside Bookings: the typed number with its
 * spaces (and any +) removed — 0412345678 or 61412345678 — which every
 * server check accepts (Locations and Settings turn a + away).
 */
export const phoneDigits = (raw = '') => normalisePhone(raw).replace(/^\+/, '');

/** A stored phone put into a phone field: spaced as if it had been typed. */
export const phoneForInput = (raw) => formatPhoneInput(raw == null ? '' : String(raw));

/**
 * A phone as shown on screen — the tablet's AuPhone.display: mobiles and
 * 13/18 numbers 0431 134 169, landlines 02 9876 5432, a 61 / +61 number as
 * its 0 form. Anything else (short, foreign, '-') comes back as given.
 */
export const displayPhone = (raw) => {
  const given = raw == null ? '' : String(raw).trim();
  let d = normalisePhone(given);
  if (!/^\+?\d+$/.test(d)) return given;
  if (d.startsWith('+')) d = d.slice(1);
  if (d.length === 11 && d.startsWith('61')) d = `0${d.slice(2)}`;
  if (d.length !== 10) return given;
  if (/^(04|05|13|18)/.test(d)) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  if (d.startsWith('0')) return `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}`;
  return given;
};

// ---- react-hook-form rules built on the checks above ----

/** Name rules for register(): required unless { required: false } (then checked only when filled). */
export const nameRules = (label = 'Name', { required = true } = {}) => ({
  validate: (v = '') => {
    if (!required && !normaliseName(v)) return true;
    return validatePersonName(v, label) || true;
  }
});

/** Phone rules for register(): any Australian number. */
export const phoneRules = (label = 'Phone number', { required = true } = {}) => ({
  validate: (v = '') => {
    if (!normalisePhone(v)) return required ? `${label} is required` : true;
    return isValidAuPhone(v) || AU_PHONE_ERROR;
  }
});

/** Email rules for register(). */
export const emailRules = ({ required = true } = {}) => ({
  validate: (v = '') => {
    if (!v.trim()) return required ? 'Email is required' : true;
    return isValidEmail(v) || EMAIL_ERROR;
  }
});
